import React, { useContext, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core';
import { HomeMember } from '@moderepo/mode-internal-apis';
import { DialogResponse, DialogResponseCode, DialogType, hideSlideOutPanel, modeUIContext, showDialog, showErrorNotification,
    showSuccessNotification } from '@moderepo/mode-ui-state';
import { BaseCompAction, capitalizeAll, SlideOutComp } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchHomeMembers, userAppDataContext, removeHomeMember, selectHomeMember, selectHomeMembers } from '@moderepo/mode-data-state-user-app';
import { useRitzTranslation } from '../../utils/customHooks';
import { HomeMemberInfoContainer } from './HomeMemberInfoContainer';
import { RouteHelper } from '../routes/RouteHelper';


const useStyle = makeStyles((theme: Theme) => {
    return {
        secondaryAction: {
            '& .icon': {
                color: theme.palette.primary.main,
            },
            '&.remove': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
});


interface HomeMemberInfoPreviewContainerProps {
    readonly projectId: number;
    readonly memberId: number | undefined;
    readonly homeId: number;
}


/**
 * This Component is used for displaying MemberInfo in the preview panel for preview.
 */
export const HomeMemberInfoPreviewContainer = (props: HomeMemberInfoPreviewContainerProps) => {
    const navigate = useNavigate();
    const { projectId } = props;
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, projectId);
    const routeHelper = RouteHelper.getInstance(navigate);
    const { trans } = useRitzTranslation(project);
    const isNewMember = props.memberId === undefined;
    const members = selectHomeMembers(appDataState, props.homeId);
    const selectedMember = props.memberId ? selectHomeMember(appDataState, props.memberId) : undefined;
    const classes = useStyle();


    /**
     * Load all the home's members
     */
    useEffect(() => {
        if (!members) {
            appDataDispatch(fetchHomeMembers(props.homeId)).catch(() => {
                // do nothing
            });
        }
    }, [props.homeId, members, appDataDispatch]);



    /**
     * Tka the user to the User home page
     */
    const viewMemberDetails = useCallback((userId: number) => {
        routeHelper.gotoUserPage({ projectId, userId });
        uiDispatch(hideSlideOutPanel());
    }, [routeHelper, projectId, uiDispatch]);


    /**
     * Unselect the selected member and update the URL
     */
    const unselectMember = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);



    const onNewMemberCanceled = useCallback(() => {
        unselectMember();
    }, [unselectMember]);



    const onNewMemberCreated = useCallback(() => {
        unselectMember();
    }, [unselectMember]);


    /**
     * Remove member from the home
     */
    const onRemoveMemberClicked = useCallback((member: HomeMember) => {
        // Confirm before removing member
        if (members) {
            if (members.length <= 1) {
                uiDispatch(showErrorNotification('error.message.last_owner'));
            } else {
                uiDispatch(showDialog({
                    type          : DialogType.CONFIRM,
                    title         : 'dialog.confirm_remove_member.title',
                    message       : 'dialog.confirm_remove_member.message',
                    messageData   : { member_name: (member.name ? member.name : `#${member.userId}`) },
                    positiveButton: 'label.remove',
                    negativeButton: 'label.cancel',
                    onClose       : async (response?: DialogResponse): Promise<void> => {
                        if (response && response.code === DialogResponseCode.POSITIVE) {
                            appDataDispatch(removeHomeMember(props.homeId, member.userId)).then(() => {
                                uiDispatch(showSuccessNotification('notif.generic_item_removed_successfully', {
                                    term: capitalizeAll(trans('terms.member')),
                                }));

                                // member removed, we need to unset the current selected member as selected entity
                                unselectMember();
                            }).catch(() => {
                                // ignore error
                            });
                        }
                    },
                }));
            }
        }
    }, [members, uiDispatch, appDataDispatch, props.homeId, trans, unselectMember]);



    /**
     * use useMemo to create the the newMemberSettings so that this doesn't get created on every re-render
     */
    const newMemberSettings = useMemo(() => {
        return {
            onNewMemberCreated,
            onNewMemberCanceled,
        };
    }, [onNewMemberCreated, onNewMemberCanceled]);


    const primaryActions = useMemo(() => {
        if (selectedMember) {
            const actions: BaseCompAction[] = [{
                label  : capitalizeAll(trans('label.view_member_details')),
                onClick: () => {
                    viewMemberDetails(selectedMember.userId as number);
                },
            }];

            return actions;
        }

        return undefined;
    }, [selectedMember, trans, viewMemberDetails]);


    const secondaryActions = useMemo(() => {
        if (selectedMember) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.secondaryAction, 'remove'),
                icon     : 'delete',
                label    : capitalizeAll(trans('label.remove_member')),
                onClick  : () => {
                    onRemoveMemberClicked(selectedMember);
                },
            }];

            return actions;
        }

        return undefined;
    }, [classes.secondaryAction, onRemoveMemberClicked, selectedMember, trans]);


    const slideOutCompContent = useMemo(() => {
        return (
            <HomeMemberInfoContainer
                className="transparent"
                infoCompProps={{
                    formProps: {
                        className: 'single-column',
                    },
                }}
                projectId={projectId}
                homeId={props.homeId}
                userId={props.memberId}
                newMemberSettings={
                    isNewMember ? newMemberSettings : undefined
                }
            />
        );
    }, [isNewMember, newMemberSettings, projectId, props.homeId, props.memberId]);


    return (
        <SlideOutComp
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
            compContent={slideOutCompContent}
            closable={!isNewMember}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
